import { AudioProxyClient } from './client';
import { AudioServiceConfig, StreamInfo } from './types';

export class ElectronAudioService {
  private client: AudioProxyClient;
  private isElectron: boolean;
  private electronAPI: any;

  constructor(config?: AudioServiceConfig) {
    this.isElectron = this.detectElectron();
    
    if (this.isElectron && (window as any).electronAPI) {
      this.electronAPI = (window as any).electronAPI;
    }

    this.client = new AudioProxyClient({
      proxyUrl: config?.audioOptions?.proxyUrl || 'http://localhost:3001',
      autoDetect: config?.audioOptions?.autoDetect ?? true,
      fallbackToOriginal: config?.audioOptions?.fallbackToOriginal ?? true,
      retryAttempts: config?.audioOptions?.retryAttempts || 3,
      retryDelay: config?.audioOptions?.retryDelay || 1000,
    });
  }

  private detectElectron(): boolean {
    if (typeof window === 'undefined') return false;
    
    return !!(window as any).electron || 
           !!(window as any).process?.versions?.electron ||
           !!(window as any).electronAPI;
  }

  public async getStreamableUrl(originalUrl: string): Promise<string> {
    if (!this.isElectron) {
      // Not in Electron, use standard client
      return this.client.getPlayableUrl(originalUrl);
    }

    // Handle local files
    if (!originalUrl.startsWith('http://') && !originalUrl.startsWith('https://')) {
      return this.handleLocalFile(originalUrl);
    }

    // For external URLs in Electron
    const isDevelopment = this.isDevelopmentMode();
    
    // Electron generally has better codec support than WebKit
    // But may still need proxy for CORS in development
    if (isDevelopment) {
      const proxyAvailable = await this.client.isProxyAvailable();
      if (proxyAvailable) {
        return this.client.getPlayableUrl(originalUrl);
      }
    }

    // In production or if proxy not available, try direct
    return originalUrl;
  }

  private handleLocalFile(filePath: string): string {
    // Convert to file:// protocol if needed
    if (filePath.startsWith('/') || filePath.match(/^[A-Za-z]:\\/)) {
      return `file://${filePath}`;
    }
    
    if (filePath.startsWith('file://')) {
      return filePath;
    }

    // If electron API provides file handling
    if (this.electronAPI?.getFileUrl) {
      return this.electronAPI.getFileUrl(filePath);
    }

    return filePath;
  }

  public async getStreamInfo(url: string): Promise<StreamInfo> {
    // If Electron provides stream checking
    if (this.electronAPI?.checkAudioStream) {
      try {
        return await this.electronAPI.checkAudioStream(url);
      } catch (error) {
        console.error('[ElectronAudioService] Failed to get stream info:', error);
      }
    }

    return this.client.canPlayUrl(url);
  }

  public async enableProtocolInterception(): Promise<void> {
    // This would be implemented in the main process
    if (this.electronAPI?.enableAudioProtocol) {
      await this.electronAPI.enableAudioProtocol();
    }
  }

  private isDevelopmentMode(): boolean {
    if (typeof window === 'undefined') return false;
    
    const isDev = process.env.NODE_ENV === 'development' ||
                  window.location?.hostname === 'localhost' ||
                  window.location?.hostname === '127.0.0.1';
    
    return isDev;
  }

  public isInElectron(): boolean {
    return this.isElectron;
  }

  public async checkSystemCodecs(): Promise<{
    supportedFormats: string[];
    missingCodecs: string[];
  }> {
    const audio = new Audio();
    const formats = [
      { mime: 'audio/mpeg', name: 'MP3' },
      { mime: 'audio/ogg', name: 'OGG' },
      { mime: 'audio/wav', name: 'WAV' },
      { mime: 'audio/webm', name: 'WebM' },
      { mime: 'audio/aac', name: 'AAC' },
      { mime: 'audio/flac', name: 'FLAC' },
    ];

    const supportedFormats: string[] = [];
    const missingCodecs: string[] = [];

    formats.forEach(format => {
      const canPlay = audio.canPlayType(format.mime);
      if (canPlay === 'probably' || canPlay === 'maybe') {
        supportedFormats.push(format.name);
      } else {
        missingCodecs.push(format.name);
      }
    });

    return { supportedFormats, missingCodecs };
  }
}